// INBOUND_TRACE_SOURCES groups the sources that produce inbound (consumer →
// ndc-json-api) traces. Phase 1A (`feat/disable-legacy-tracer` in
// ndc-json-api) turned off the in-process tracer for the inbound leg and
// the trace-masker sidecar took over, emitting `Source = "sidecar"`. Older
// rows still carry `Source = "json-api"` within the 90-day retention
// window, so the JSON-API panel must accept both.
export const INBOUND_TRACE_SOURCES = ['json-api', 'sidecar'] as const;
export type InboundTraceSource = (typeof INBOUND_TRACE_SOURCES)[number];
export type FilterableTraceSource = InboundTraceSource | 'requester';

export const isInboundTraceSource = (source: string | undefined): boolean =>
  source === 'json-api' || source === 'sidecar';

export interface Trace {
  id: number;
  request_id: number;
  providers: string;
  status: string;
  source: string;
  /**
   * Human-readable discriminator extracted server-side from the inbound
   * AirShopping message body: cabin name for NDC providers, fare-brand
   * name for Kyte. Used by the Request Details view to tell parallel
   * same-provider requests apart. Empty for non-AirShopping traces and
   * historical rows that predate migration 59.
   */
  label?: string;
  /**
   * Per-call NDC method of one outbound provider leg ("OrderChange",
   * "OrderReshopReprice", …). Set on source='requester' traces written
   * after tracer migration 75; part of the backend convergence key so
   * SEQUENTIAL same-provider calls under one inbound request land on
   * separate traces instead of overwriting one. Rendered to tell
   * sequential sibling traces apart (they share providers and usually
   * have no label). Empty/undefined for inbound traces and pre-75 rows.
   */
  method?: string;
  messages: Message[];
  created_at: string;
  order?: number;
}

// Per-request aggregate status. Server-computed (worst-of across this
// request's inbound + requester legs), persisted on requests.aggregate_status
// by services.RecomputeSessionStatus on every trace write. Mirrors the
// FE GetStatusPipe rule 1:1, including the AirShopping exception.
//
// Authoritative for row colour because the agent role projection in
// GetSession filters non-inbound traces, which would otherwise make a
// FE-side walk disagree with the server-aggregated session status.
export type RequestAggregateStatus = 'ok' | 'warning' | 'error' | 'not_set';

export interface Request {
  id: number;
  application: string;
  consumer: string;
  request_id: string;
  session_id: number;
  method: string;
  provider_method: string;
  agent: string;
  agency: string;
  is_collapsed: boolean;
  traces: Trace[];
  created_at: string;
  provider?: string;
  version_id?: string;
  status: string;
  aggregate_status?: RequestAggregateStatus;
}

// Session-level status, server-computed by services.RecomputeSessionStatus
// (tracer/app/services/session_status.go) on every trace write. Mirrors
// the four buckets the home-page status pill renders.
//   pending       — no requests yet, or no traces written for any request
//   clean         — every request has traces and none errored
//   with_warnings — at least one request has a partial requester failure
//   with_errors   — at least one request errored at the inbound leg, or
//                   every requester leg of some request errored
export type SessionStatus = 'pending' | 'clean' | 'with_warnings' | 'with_errors';

export interface Session {
  id: number;
  value: string;
  created_at: string;
  updated_at: string;
  requests: Request[];
  important: boolean;
  status: SessionStatus;
}

export interface Version {
  id: number;
  env: string;
  hash: string;
  message: string;
  created_at: string;
}

export interface Message {
  created_at: string;
  format: string;
  id: number;
  name: string;
  trace_id: number;
}

// Three response shapes the backend's /message/:id can return,
// matching tracer/app/controllers/page_controller.go:GetMessage
// (inlineDisplayLimit / onDemandDisplayLimit tiers):
//
//   Tier 1 (auto-display, ≤ 250 KB):
//     { body, headers, size }
//
//   Tier 2 (display on demand, ≤ 1 MB):
//     { presigned_url, size, display_on_demand: true, max_inline_bytes }
//
//   Tier 3 (zip-only, > 1 MB):
//     { size, zip_only: true, warning, max_inline_bytes,
//       max_ondemand_bytes }   ← no presigned URL, no body
//
// All fields after `body`/`headers` are optional so the type covers
// every tier without forcing the consumer to narrow each field
// individually.
export interface MessageResponse {
  body?: string;
  headers?: Record<string, string>;
  presigned_url?: string;
  display_on_demand?: boolean;
  zip_only?: boolean;
  size?: number;
  warning?: string;
  max_inline_bytes?: number;
  max_ondemand_bytes?: number;
  error?: string;
}

export interface TracerResponse {
  sessions: Session[];
  error?: string;
}

export interface SessionResponse {
  error?: string;
  // providers describes the REQUESTS IN THIS PAGE, not the whole
  // session. Session-wide chips would require a DISTINCT over every
  // trace in the session, which on the largest real session measures
  // 28.5s server-side — see GetSession in page_controller.go.
  providers: string[];
  requests: Request[];
  session: Session;
  versions?: { [key: string]: Version };
  // Paging. Absent on responses from a tracer older than the
  // session-paging change, hence all optional.
  requests_total?: number;
  per_page?: number;
  // next_cursor is an opaque token for the next chunk in the direction of
  // travel, empty when there is none left. The rail walks forward
  // (order=asc), so it is fed back as `after` and the chunk it names is
  // the NEWER one.
  next_cursor?: string;
}
